import React, { useState, useEffect } from 'react';
import { RDS } from 'ouisys-engine/strategy';
import { Button, Link, defineMessages } from 'ouisys-component-library';
import { PINEntryFailure, PINEntrySuccess, backToStartAction, usePinFlowState } from 'ouisys-engine/pinFlow';
import Loader from '../Loader/index';
import ThankYouStep from '../ThankYouStep';
import { FormattedMessage, injectIntl } from '../../localization';
import { useRootContext } from '../../providers/RootContext';
import { useDispatch } from '../../__doNotModify/store';
import { useSelector } from 'react-redux';
import ErrorModal from '../ErrorModal';

const defaultMessages = defineMessages({
  PinInputPlaceholder: {
    id: "PinInputPlaceholder",
    defaultMessage: "PIN",
  },
});

interface PINEntryStepProps {
  rds: RDS.RemoteDataState<PINEntryFailure, PINEntrySuccess>;
  onEnd: (pin: string) => void;
  intl: any;
}

const PINEntryStep = (props: PINEntryStepProps) => {
  const { intl } = props;
  const pinFlowState = usePinFlowState(useSelector);
  const { pinMaxLength } = pinFlowState?.data;
  const { config, msisdn, setShowModal } = useRootContext();
  const dispatch = useDispatch();
  const [pin, setPin] = useState<string>('');
  const backToStart = () => dispatch(backToStartAction());

  // Peru - showModal if there is an error
  const RDSisFailure: boolean = RDS.IsFailure(props.rds);
  useEffect(() => {
    if (RDSisFailure) {
      setShowModal(RDSisFailure);
    }
  }, [RDSisFailure]);

  return (
    <form
      className="PINEntryStep subscriptionArea"
      onSubmit={(ev) => {
        ev.preventDefault();
        props.onEnd(pin);
      }}
    >

      <div className="pin-entry">
        {RDS.WhenSuccess<PINEntrySuccess, JSX.Element>(
          <div>
            <div className="pinLabel">
              <FormattedMessage
                id="PinLabel"
                defaultMessage="Enter the 4-digit PIN sent to you via SMS for verification."
              />
            </div>
            <div className="pinInputWrap">
              <input
                autoFocus
                id="single-factor-code-text-field"
                className="pinInput"
                type="text"
                placeholder={intl.formatMessage(defaultMessages.PinInputPlaceholder)}
                value={pin}
                pattern="[0-9]*"
                inputMode="numeric"
                maxLength={pinMaxLength || 6}
                minLength={4}
                autoComplete="one-time-code"
                onChange={(ev) => setPin(ev.target.value)}
              />
            </div>
            <Button
              id="pin-submit-button"
              type="submit"
              className="btn submit-button"
              disabled={RDS.IsLoading(props.rds) || pin.length < 4}
            >
              <FormattedMessage id="PinButton" defaultMessage="Confirm" />
            </Button>
          </div>,
          ({ finalUrl, isAlreadySubscribed }) => {
            return (
              <ThankYouStep finalUrl={finalUrl} isAlreadySubscribed={isAlreadySubscribed} />
            );
          }
        )(props.rds)}
      </div>

      {RDS.match<PINEntryFailure, PINEntrySuccess, any>({
        failure: (err: PINEntryFailure) => (
          <ErrorModal error={err} />
        ),
        nothingYet: () => (
          <Link
            className="tryAgainLink"
            id="backToStart"
            onClick={backToStart}
          >
            <FormattedMessage
              id="TryAgain"
              defaultMessage="Change phone number"
              values={{ phone: msisdn }}
            />
          </Link>
        ),
        loading: () => (
          <div className="loading-wrapper">
            <Loader />
            <div>
              <FormattedMessage id="PleaseWait" defaultMessage="Please wait..." />
            </div>
          </div>
        ),
        success: ({ finalUrl }) => null
      })(props.rds)}

    </form>
  );
};

export default injectIntl(PINEntryStep);
